import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { Link, useNavigate } from "@tanstack/react-router";
import { Minus, Plus } from "lucide-react";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import {
    Button,
    MarkdownContent,
    ProseDocument,
    Spinner,
    TablePagination,
} from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { TrendingRow } from "components/layout/TrendingRow";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";

const formatDateKey = (date: Date, timeZone: string) =>
    new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "2-digit",
        year: "numeric",
        timeZone,
    }).format(date);

type Summary = {
    id: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    type: "NEWS" | "SOCIAL";
    title: string;
    content: string;
    contentJson?: TipTapDoc | null;
    summary?: string | null;
    featuredImageURL?: string;
};

const groupByDate = (summaries: Summary[], timeZone: string) => {
    return summaries.reduce<Record<string, Summary[]>>((acc, summary) => {
        const dateKey = formatDateKey(new Date(summary.createdAt), timeZone);
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(summary);
        return acc;
    }, {});
};

const today = new Date();

export const AIDigest = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const timeZone = useUserTimezone();
    const todayKey = formatDateKey(new Date(), timeZone);
    const [openDate, setOpenDate] = useState<string | null>(todayKey);
    const [page, setPage] = useState<number | undefined>(0);
    const [pageSize, setPageSize] = useState<number | undefined>(15);

    const [linkModal, setLinkModal] = useState(false);
    const [email, setEmail] = useState("");
    const [recipient, setRecipient] = useState("");
    const [emailError, setEmailError] = useState("");
    // Track which entry is being shared
    const [activeEntry, setActiveEntry] = useState<Summary | null>(null);
    const { mutateAsync: shareMutateDigest, isPending: isSharing } =
        trpc.digest.shareSummary.useMutation();

    const handleShare = async () => {
        if (!activeEntry) return;

        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (!email.trim()) {
            setEmailError("Email is required.");
            return;
        }

        if (!emailRegex.test(email)) {
            setEmailError("Invalid email address.");
            return;
        }

        setEmailError("");

        try {
            await shareMutateDigest({
                email,
                recipientName: recipient,
                summaryId: activeEntry.id,
            });

            enqueueSnackbar("Summary shared.", { variant: "success" });

            setLinkModal(false);
            setEmail("");
            setRecipient("");
            setActiveEntry(null);
        } catch (error) {
            console.error(error);

            enqueueSnackbar("Failed to share summary.", { variant: "error" });
        }
    };

    const {
        data,
        isPending: isLoading,
        isError,
    } = trpc.digest.getSitewideSummaries.useQuery({
        skip: (page ?? 0) * (pageSize ?? 15),
        take: pageSize ?? 15,
    });

    if (isLoading) {
        return (
            <div className="mx-auto w-full pt-6">
                <Spinner className="mx-auto" />
            </div>
        );
    }

    if (isError || !data) {
        return <div className="py-8 text-center text-red-500">Failed to load summaries.</div>;
    }

    const groupedSummaries = groupByDate(data.summaries, timeZone);
    const sortedDates = Object.keys(groupedSummaries).sort((a, b) => {
        const aFirst = groupedSummaries[a][0];
        const bFirst = groupedSummaries[b][0];
        return new Date(bFirst.createdAt).getTime() - new Date(aFirst.createdAt).getTime();
    });

    return (
        <div className="mx-auto w-full pt-6">
            <div className="mb-4 font-sans text-[28px] font-semibold text-[#424242]">AI Digest</div>
            <TrendingRow filterType="sitewideSummary" title="Trending Digests" limit={5} />
            <div className="mb-4 rounded-xl border border-[#92CEE4] bg-[#DFF1FA] p-5 font-sans text-[16px] font-normal text-[#424242]/90">
                <h2 className="font-semibold">Here’s Your Daily News Digest</h2>
                <p className="">A concise summary of today’s top headlines to keep you informed.</p>
            </div>

            {sortedDates.map((dateKey) => {
                const entries = groupedSummaries[dateKey];
                return (
                    <div
                        key={dateKey}
                        className="mb-4 w-full overflow-hidden rounded-2xl border border-[#c8c8c8] bg-[#fafffe] p-5"
                    >
                        <button
                            onClick={() => setOpenDate(openDate === dateKey ? null : dateKey)}
                            className="flex w-full items-center justify-between"
                        >
                            <span className="truncate font-sans text-[18px] font-semibold leading-[100%] text-[#424242]/90">
                                {dateKey}
                            </span>
                            <span className="ml-2 h-4 w-4 flex-shrink-0">
                                {openDate === dateKey ? (
                                    <Minus className="h-4 w-4" />
                                ) : (
                                    <Plus className="h-4 w-4" />
                                )}
                            </span>
                        </button>
                        {openDate === dateKey && (
                            <div className="p-0">
                                {entries.map((entry) => (
                                    <div
                                        key={entry.id}
                                        className="mt-6 flex gap-3 break-words rounded-sm border-l-[3px] border-[#2da7e0] bg-white px-3 py-3 text-sm text-[#424242] shadow-md"
                                    >
                                        {entry.featuredImageURL && (
                                            <Link
                                                to="/digest/$id"
                                                params={{ id: String(entry.id) }}
                                                aria-label={entry.title}
                                                className="block aspect-square w-24 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100 sm:w-32"
                                            >
                                                <img
                                                    src={entry.featuredImageURL}
                                                    alt={entry.title}
                                                    className="h-full w-full object-cover"
                                                />
                                            </Link>
                                        )}
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            <div className="flex items-start justify-between gap-3">
                                                <Link
                                                    to="/digest/$id"
                                                    params={{ id: String(entry.id) }}
                                                    className="mb-2 font-sans text-[17px] font-semibold text-[#424242]/90 hover:text-blue-600 hover:underline"
                                                >
                                                    {entry.title}
                                                </Link>
                                                <div className="flex flex-shrink-0 items-center gap-2">
                                                    <FavoriteButton
                                                        target={{
                                                            type: "sitewideSummary",
                                                            id: entry.id,
                                                        }}
                                                        size={14}
                                                    />

                                                    {/* Share Button */}
                                                    <button
                                                        onClick={() => {
                                                            setEmail("");
                                                            setRecipient("");
                                                            setActiveEntry(entry);
                                                            setLinkModal(true);
                                                        }}
                                                        className="rounded-lg bg-gray-100 px-3 py-1 text-xs"
                                                    >
                                                        Share
                                                    </button>
                                                </div>
                                            </div>

                                            <p className="whitespace-pre-line break-words">
                                                {entry.contentJson ? (
                                                    <ProseDocument
                                                        doc={entry.contentJson}
                                                        onShowMore={() =>
                                                            navigate({
                                                                to: "/digest/$id",
                                                                params: { id: String(entry.id) },
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <MarkdownContent
                                                        markdown={entry.content}
                                                        onShowMore={() =>
                                                            navigate({
                                                                to: "/digest/$id",
                                                                params: { id: String(entry.id) },
                                                            })
                                                        }
                                                    />
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            <div>
                <TablePagination
                    total={data.totalCount}
                    page={page}
                    pageSize={pageSize}
                    onChangePage={setPage}
                    onChangePageSize={setPageSize}
                    message="Items per page"
                />
            </div>

            {linkModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-4">
                        <h2 className="mb-3 font-sans text-[24px] font-bold text-[#424242]">
                            Share Summary
                        </h2>

                        <hr className="-mx-[16px] mb-5 border-[#c8c8c8]" />

                        {/* Email */}
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Enter Email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError("");
                                }}
                                className={`w-full rounded-lg border bg-[#eeeeee] px-3 py-2 text-sm outline-none ${
                                    emailError
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-blue-500"
                                }`}
                            />

                            {emailError && (
                                <p className="mt-1 text-sm text-red-500">{emailError}</p>
                            )}
                        </div>

                        {/* Recipient */}
                        <input
                            type="text"
                            placeholder="Enter Recipient Name"
                            value={recipient}
                            onChange={(e) => setRecipient(e.target.value)}
                            className="mb-4 w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm outline-none"
                        />

                        {/* Actions */}
                        <div className="flex justify-end">
                            <Button
                                onClick={handleShare}
                                disabled={isSharing}
                                isLoading={isSharing}
                                variant="primary"
                            >
                                {isSharing ? "Sharing" : "Share"} <SubmitIcon className="ml-2" />
                            </Button>
                        </div>

                        {/* Close */}
                        <button
                            className="absolute right-4 top-4"
                            onClick={() => {
                                setLinkModal(false);
                                setActiveEntry(null);
                            }}
                        >
                            <CloseIcon className="h-[30px] w-[30px]" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
